import { Dispatch, FC, SetStateAction, useLayoutEffect, useRef } from 'react';
import { defaultFn } from 'constants/index';
import { Button, BUTTON_SIZE_TYPES, BUTTON_STATE_TYPES, BUTTON_TYPES, Input, SvgSpriteLoader, Text } from 'destiny';
import { COLORS } from 'destiny/dist/constants/colors';
import { ICON_SPRITE_TYPES } from 'destiny/dist/constants/icons';
import { defaultFnType } from 'destiny/dist/types';
import { OptionsType } from 'destiny/dist/types/dropdown';
import { EMPTY_TOAST, SIGNUP_STEPS } from 'modules/auth/auth.constants';
import { SignUpInfoType } from 'modules/auth/auth.types';
import { trackMixpanel } from 'utils/mixpanel';
import CountryPicker from 'components/country-picker/CountryPicker';
import DashedBorder from 'components/icons/DashedBorder';
import { ButtonLoader } from 'components/loader/ButtonLoader';

interface SignUpMultiEntityFormPropsType {
  setSignUpInfo: Dispatch<SetStateAction<SignUpInfoType>>;
  signUpInfo: SignUpInfoType;
  setToast: Dispatch<SetStateAction<{ hidden: boolean; isError: boolean; message: string }>>;
  setCurrentStep: Dispatch<SetStateAction<SIGNUP_STEPS>>;
  isLoading: boolean;
  attemptSignup: defaultFnType;
  scrollToBottom?: defaultFnType;
}

const SignUpMultiEntityForm: FC<SignUpMultiEntityFormPropsType> = ({
  signUpInfo,
  setSignUpInfo,
  setCurrentStep,
  setToast,
  isLoading,
  attemptSignup,
  scrollToBottom = defaultFn,
}) => {
  const lastInputRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    lastInputRef?.current?.focus();
  }, []);

  const handleAddEnitity = () => {
    setSignUpInfo((prev) => ({
      ...prev,
      entityDetails: [...prev.entityDetails, { name: '', geography: '', country: '' }],
    }));
    setTimeout(() => {
      scrollToBottom?.();
      lastInputRef?.current?.focus();
    }, 0);
  };

  const handleDeleteEnitity = (id: number) => {
    setSignUpInfo((prev) => ({ ...prev, entityDetails: prev.entityDetails.filter((_, idx) => idx !== id) }));
  };

  const handleEntityDetailsChange = (value: string | object, key: string, idx: number) => {
    setSignUpInfo((prev) => ({
      ...prev,
      entityDetails: prev?.entityDetails?.map((val, index) => (index === idx ? { ...val, [key]: value } : val)),
    }));
  };

  const handleBack = () => {
    setCurrentStep(SIGNUP_STEPS.USER_DETAILS);
    setToast(EMPTY_TOAST);
  };

  return (
    <div className='tw-mt-9'>
      <div className='tw-flex tw-w-full tw-gap-4 f-14-500'>
        <Text textClass='tw-w-[222px]' id='sign-up-multi-entity-form-business-name'>
          Business name
        </Text>
        <Text id='sign-up-multi-entity-form-business-country'>Business registration country</Text>
      </div>
      {signUpInfo.entityDetails.map((val, idx) => (
        <div key={idx} className='tw-flex tw-flex-col md:tw-flex-row tw-gap-4'>
          <Input
            inputFieldWrapperClass='!tw-my-2 tw-w-[222px]'
            wrapperClass='tw-w-[222px]'
            inputFieldProps={{
              inputTagProps: {
                placeHolder: '',
                name: 'merchantName',
                value: val.name,
                onChange: (e) => handleEntityDetailsChange(e.target.value, 'name', idx),
                id: 'SIGNUP_MERCHANT_NAME_INPUT',
                inputRef: idx === signUpInfo?.entityDetails?.length - 1 ? lastInputRef : undefined,
              },
              size: 'MEDIUM',
            }}
            showLabel
          />
          <CountryPicker
            className='tw-my-2'
            labelClassName='f-14-500'
            id='countryCode'
            isControlled
            onMenuItemClick={(countryCode: OptionsType[]) => {
              handleEntityDetailsChange(countryCode[0].value.toString(), 'country', idx);
              handleEntityDetailsChange(countryCode[0]?.metadata?.code, 'geography', idx);
            }}
            overrideClassName='tw-w-full'
            showDialCode={false}
            selectedValueKey='name'
            value={{
              label: signUpInfo.entityDetails[idx]?.country ?? 'Select',
              value: signUpInfo.entityDetails[idx]?.country ?? 'Select',
            }}
          />
          {idx !== 0 && (
            <div
              role='button'
              tabIndex={0}
              onKeyDown={(e) => e.code === 'Enter' && handleDeleteEnitity(idx)}
              onClick={() => handleDeleteEnitity(idx)}
              className='tw-border tw-border-DIVIDER_GRAY tw-rounded-lg tw-my-2 tw-px-3 tw-py-3 tw-cursor-pointer active:tw-scale-95'
            >
              <SvgSpriteLoader
                id='trash-01'
                iconCategory={ICON_SPRITE_TYPES.GENERAL}
                width={24}
                height={24}
                color={COLORS.TEXT_SECONDARY}
              />
            </div>
          )}
        </div>
      ))}
      <div
        role='button'
        tabIndex={0}
        onClick={handleAddEnitity}
        onKeyDown={(e) => e.code === 'Enter' && handleAddEnitity()}
        className='tw-mt-2 tw-border tw-border-DIVIDER_GRAY tw-flex tw-justify-center tw-items-center tw-py-[18px] tw-rounded-xl tw-cursor-pointer tw-duration-75 active:tw-scale-y-95'
      >
        <SvgSpriteLoader id='plus' iconCategory={ICON_SPRITE_TYPES.GENERAL} width={20} height={20} />
        <Text textClass='f-14-400 tw-ml-4 tw-select-none' id='sign-up-multi-entity-form-add-another-entity'>
          Add another business entity
        </Text>
      </div>
      <div className='tw-my-8'>
        <DashedBorder />
      </div>
      <div className='tw-flex tw-gap-2'>
        <Button
          showLeadingIcon={true}
          customLeadingIcon={
            <SvgSpriteLoader id='arrow-left' iconCategory={ICON_SPRITE_TYPES.ARROWS} width={16} height={16} />
          }
          buttonProps={{
            size: BUTTON_SIZE_TYPES.MEDIUM,
            btnType: BUTTON_TYPES.SECONDARY,
            wrapperClass: 'tw-overflow-clip tw-outline-none',
            onClick: handleBack,
            id: 'SIGNUP_CREATE_ACCOUNT_BACK_BUTTON',
            eventCallback: (id, data) => {
              trackMixpanel(id, { ...data });
            },
          }}
        >
          Back
        </Button>
        <Button
          buttonProps={{
            size: BUTTON_SIZE_TYPES.MEDIUM,
            btnType: BUTTON_TYPES.PRIMARY,
            state: isLoading ? BUTTON_STATE_TYPES.DISABLED : BUTTON_STATE_TYPES.DEFAULT,
            isLoading: isLoading,
            wrapperClass: 'tw-w-40 tw-overflow-clip tw-outline-none',
            onClick: attemptSignup,
            id: 'SIGNUP_CREATE_ACCOUNT_BUTTON',
            disabled: signUpInfo.entityDetails.some((val) => !val?.name || !val?.geography),
            eventCallback: (id, data) => {
              trackMixpanel(id, { ...data });
            },
          }}
          loader={<ButtonLoader />}
        >
          Create Account
        </Button>
      </div>
    </div>
  );
};

export default SignUpMultiEntityForm;
